/**
 * 帳號管理純規則（前後端共用、無 I/O）— 2026-08-21 帳號管理批次
 * - 密碼強度
 * - 舊 session 失效判定（JWT iat vs passwordChangedAt）
 * - 管理員操作防呆（擁有者／自己／最後一位 admin／角色變更限制）
 */
import type { Role } from "./const.js";

export const PASSWORD_MIN = 8;
export const PASSWORD_MAX = 128;

export type PasswordIssue = "too_short" | "too_long" | "need_letter" | "need_digit";

/** 回傳違規清單；空陣列＝合格。規則：8–128 字、至少一個英文字母與一個數字。 */
export function passwordIssues(pw: string): PasswordIssue[] {
  const issues: PasswordIssue[] = [];
  if (pw.length < PASSWORD_MIN) issues.push("too_short");
  if (pw.length > PASSWORD_MAX) issues.push("too_long");
  if (!/[A-Za-z]/.test(pw)) issues.push("need_letter");
  if (!/\d/.test(pw)) issues.push("need_digit");
  return issues;
}

export const isPasswordValid = (pw: string): boolean => passwordIssues(pw).length === 0;

/**
 * JWT 的 iat 是「秒」；passwordChangedAt 是 ms。
 * 與改密碼同一秒內簽出的 token（改密碼當下重簽的那顆）視為有效，更早的失效。
 */
export function isSessionRevoked(tokenIatSec: number | undefined, passwordChangedAtMs: number | null | undefined): boolean {
  if (!passwordChangedAtMs || tokenIatSec === undefined) return false;
  return tokenIatSec < Math.floor(passwordChangedAtMs / 1000);
}

/**
 * 帳號管理的階層（2026-08-26）：
 * - admin（supervisor）：管得動所有角色
 * - dispatcher：只管得動需求方 / 供應方
 * - agency / fleet：不能管理任何帳號
 * 僅判斷「角色階層」；擁有者／自己／最後一位 admin 等防呆另由 adminMutationGuard 處理。
 */
export function canManageUser(actorRole: Role, targetRole: Role): boolean {
  if (actorRole === "admin") return true;
  if (actorRole === "dispatcher") return targetRole === "agency" || targetRole === "fleet";
  return false;
}

/** 該操作者能建立／管理哪些角色（前端下拉選單與後端驗證共用） */
export function manageableRoles(actorRole: Role): Role[] {
  return (["admin", "dispatcher", "agency", "fleet"] as Role[]).filter((r) => canManageUser(actorRole, r));
}

/** 角色變更（admin ↔ dispatcher）僅 admin 可執行 */
export function canActorChangeRole(actorRole: Role): boolean {
  return actorRole === "admin";
}

/** 角色變更只允許後台角色互換；agency / fleet 綁 party 列，不可改。 */
export function canChangeRole(from: Role, to: Role): boolean {
  if (from === to) return true;
  const backoffice: Role[] = ["admin", "dispatcher"];
  return backoffice.includes(from) && backoffice.includes(to);
}

export interface AdminGuardTarget {
  id: number;
  role: Role;
  status: "active" | "inactive" | "suspended";
  isOwner: "yes" | "no";
}

export type AdminGuardReason = "owner_protected" | "self_protected" | "last_admin" | "role_change_not_allowed";

/**
 * 管理員對某使用者做「停用」或「改角色」前的防呆。
 * - activeAdminCount：目前啟用中的 admin 人數（含 target 本人若符合）。
 */
export function adminMutationGuard(
  actorId: number,
  target: AdminGuardTarget,
  change: { status?: "active" | "suspended"; role?: Role },
  activeAdminCount: number,
): AdminGuardReason | null {
  const demoting = change.role !== undefined && change.role !== target.role;
  const suspending = change.status === "suspended" && target.status !== "suspended";

  if (demoting && !canChangeRole(target.role, change.role!)) return "role_change_not_allowed";
  if (target.isOwner === "yes" && (demoting || suspending)) return "owner_protected";
  if (target.id === actorId && (demoting || suspending)) return "self_protected";

  const removesActiveAdmin =
    target.role === "admin" && target.status === "active" && (suspending || (demoting && change.role !== "admin"));
  if (removesActiveAdmin && activeAdminCount <= 1) return "last_admin";
  return null;
}

/** 臨時密碼字元集：去掉易混淆的 0/O/1/l/I。 */
export const TEMP_PASSWORD_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
export const TEMP_PASSWORD_LENGTH = 12;

/**
 * 由呼叫端提供亂數位元組（server 用 crypto.randomBytes），確保至少含一個字母與一個數字。
 * 純函式：同樣的 bytes 得到同樣的結果，方便測試。
 */
export function temporaryPasswordFromBytes(bytes: Uint8Array): string {
  if (bytes.length < TEMP_PASSWORD_LENGTH + 2) throw new Error("need at least 14 random bytes");
  const chars: string[] = [];
  for (let i = 0; i < TEMP_PASSWORD_LENGTH; i++) {
    chars.push(TEMP_PASSWORD_ALPHABET[bytes[i]! % TEMP_PASSWORD_ALPHABET.length]!);
  }
  const letters = TEMP_PASSWORD_ALPHABET.replace(/\d/g, "");
  const digits = TEMP_PASSWORD_ALPHABET.replace(/\D/g, "");
  if (!/[A-Za-z]/.test(chars.join(""))) chars[0] = letters[bytes[TEMP_PASSWORD_LENGTH]! % letters.length]!;
  if (!/\d/.test(chars.join(""))) chars[1] = digits[bytes[TEMP_PASSWORD_LENGTH + 1]! % digits.length]!;
  return chars.join("");
}

export const PASSWORD_RESET_TTL_MS = 60 * 60 * 1000;
export const PASSWORD_RESET_MAX_PER_HOUR = 3;

/** 重設 token 是否仍可用 */
export function isResetTokenUsable(
  t: { expiresAt: number; usedAt: number | null },
  nowMs: number,
): boolean {
  return t.usedAt == null && t.expiresAt > nowMs;
}
